import { isFunction } from 'lodash';
import React from 'react';
import cn from 'classnames';
import { Slider as AntdSlider } from 'antd';
import { SliderSingleProps, SliderRangeProps } from 'antd/lib/slider';
import { Errors } from '../Errors';
import { Label, LabelProps } from '../Label';
import { ReduxFieldInputTypes, ReduxFieldMetaTypes } from '../utils';
import css from './Slider.scss';

type AntdSliderProps = Omit<SliderSingleProps | SliderRangeProps, 'onAfterChange'>;

type SliderProps = {
	input: ReduxFieldInputTypes;
	meta: ReduxFieldMetaTypes;
	theme?: 'dark' | 'light';
	className?: string;
	label?: LabelProps;
	showErrorsOn?: string;
} & AntdSliderProps;

type PureSliderProps = {
	value: AntdSliderProps['value'];
	defaultValue?: AntdSliderProps['defaultValue'];
	id?: string;
	label?: SliderProps['label'];
	theme?: SliderProps['theme'];
	className?: string;
	sliderProps?: AntdSliderProps;
};

export const SliderPure = ({ label, theme = 'light', ...props }: PureSliderProps) => (
	<label className={cn(css.slider, css[theme], props.className, { [css.disabled]: props.sliderProps?.disabled })}>
		{label && (
			<Label
				theme={theme}
				htmlFor={props.id}
				component="div"
				{...label}
				className={cn(css.label, label.className)}
			/>
		)}
		<div className={cn(css.inner)}>
			<AntdSlider
				{...(props.sliderProps as SliderSingleProps)}
				id={props.id}
				defaultValue={props.defaultValue as number}
				value={props.value as number}
			/>
		</div>
	</label>
);

export const Slider = ({
	className,
	input,
	label,
	meta,
	theme = 'light',
	showErrorsOn = 'touched',
	...props
}: SliderProps) => {
	const id = `${meta.form}.${input.name}`;
	const showErrors = isFunction(showErrorsOn) ? showErrorsOn(meta, input) : meta[showErrorsOn || ''];

	return (
		<div className={cn(css.fieldSlider, className)}>
			<SliderPure
				id={id}
				value={input.value}
				defaultValue={input.value}
				label={label}
				sliderProps={props}
				theme={theme}
			/>
			<Errors show={showErrors}>{meta.error}</Errors>
		</div>
	);
};

/*
 * Example
 * name - form values key
 * prop - value to assign to form {key: value}

<Field
	name="minMax"
	component={Slider}
	label={{ children: 'Range', component: 'label' }}
	range
	min={1}
	max={10}
/>
*/
